import React, { useCallback, useState } from "react";
import { Publications } from "../publications";
import { Button, Popover, Skeleton, Typography } from "@mui/material";
import { LaunchIcon } from "shared/icons/LaunchIcon";
import { useAppsData } from "../hooks/useAppsData";
import { getClasses } from "shared/helpers/styleHelper";
import { ConfettiIcon } from "shared/icons/ConfettiIcon";
import { AppOption } from "./AppOption";
import { publicationsClient } from "../client/publicationsClient";

export const PublishButton = (props: PublishButtonProps) => {
  const { publications, surveyId, onPublishChange } = props;

  const { apps, isLoading } = useAppsData();
  const [selectedApps, setSelectedApps] = useState<(string | number)[]>([]);
  const [isPublishing, setIsPublishing] = useState<boolean>(false);
  const [isPublished, setIsPublished] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
    setSelectedApps([]);
  };

  const handleCheckChange = useCallback((isChecked: boolean, appId: string | number) => {
    if (isChecked) {
      setSelectedApps((old) => [...old, appId]);
    } else {
      setSelectedApps((old) => old.filter((id) => id !== appId));
    }
  }, []);

  const handlePublish = useCallback(async () => {
    if (!surveyId) {
      return;
    }

    setIsPublished(false);
    setIsPublishing(true);
    handleClose();

    try {
      for (let i = 0; i < selectedApps.length; i++) {
        const publication = await publicationsClient.publishSurvey(selectedApps[i], surveyId);
        onPublishChange(undefined, publication.data);
      }
      setIsPublishing(false);
      setIsPublished(true);
    } catch (e) {
      console.error(e);
      setIsPublishing(false);
    }
  }, [onPublishChange, selectedApps, surveyId]);

  return (
    <>
      <Button
        onClick={handleClick}
        className={getClasses({
          "layout-publish-btn": true,
          "is-loading": isPublishing,
          "is-success": isPublished,
        })}
      >
        <div className="btn-icon-wrapper">
          <LaunchIcon className="main-icon" />
          <ConfettiIcon className="success-icon" />
        </div>
        Publish
      </Button>

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        slotProps={{ paper: { className: "layout-publish-btn-popover" } }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <div className="_d-grid-sm-gap">
          <Typography variant="h4" color="text.tertiary" mb={1}>
            Select where to publish
          </Typography>

          {isLoading ? (
            <Skeleton />
          ) : (
            apps.map((app) => (
              <AppOption
                isChecked={selectedApps.includes(app.id)}
                onCheckChange={(isChecked) => handleCheckChange(isChecked, app.id)}
                app={app}
                key={app.id}
                onUnpublished={(appId) => onPublishChange(appId)}
                publication={publications.find((p) => p.application.id === app.id)}
              />
            ))
          )}
        </div>

        <div className="_d-flex-ali-center-jc-end-sm-gap">
          <Button size="small" color="secondary" onClick={handleClose}>
            Close
          </Button>
          <Button size="small" onClick={handlePublish} disabled={!selectedApps.length}>
            Publish to selected apps
          </Button>
        </div>
      </Popover>
    </>
  );
};

interface PublishButtonProps {
  publications: Publications.Publication[];
  surveyId?: string;
  onPublishChange: (unpublishedAppId?: string | number, newPublication?: Publications.Publication) => void;
}
